/**
 * Remotion Carousel Slide Composition
 *
 * Main composition that renders a carousel slide with all overlays.
 * This is used by both the Player (preview) and renderStill (export).
 */

import React from 'react';
import { AbsoluteFill, Img } from 'remotion';
import { CarouselSlideProps, OverlayData } from '../types';
import { TextOverlay } from './TextOverlay';
import { ImageOverlay } from './ImageOverlay';

export const CarouselSlide: React.FC<CarouselSlideProps> = ({
  width,
  height,
  backgroundColor,
  backgroundImage,
  overlays,
}) => {
  // Sort overlays by zIndex
  const sortedOverlays = [...overlays].sort(
    (a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0)
  );

  return (
    <AbsoluteFill
      style={{
        backgroundColor,
        width,
        height,
      }}
    >
      {/* Background image if provided */}
      {backgroundImage && (
        <Img
          src={backgroundImage}
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      )}

      {/* Render all overlays */}
      {sortedOverlays.map((overlay) => (
        <OverlayRenderer
          key={overlay.id}
          overlay={overlay}
          containerWidth={width}
          containerHeight={height}
        />
      ))}
    </AbsoluteFill>
  );
};

// Helper component to render the correct overlay type
const OverlayRenderer: React.FC<{
  overlay: OverlayData;
  containerWidth: number;
  containerHeight: number;
}> = ({ overlay, containerWidth, containerHeight }) => {
  switch (overlay.type) {
    case 'text':
      return (
        <TextOverlay
          overlay={overlay}
          containerWidth={containerWidth}
          containerHeight={containerHeight}
        />
      );
    case 'image':
      return (
        <ImageOverlay
          overlay={overlay}
          containerWidth={containerWidth}
          containerHeight={containerHeight}
        />
      );
    default:
      return null;
  }
};

export default CarouselSlide;
